'use client';

import { useEffect, useRef, ReactNode } from 'react';

// Shared observer pool - one observer per unique threshold value
const observers = new Map<number, IntersectionObserver>();
const callbacks = new Map<Element, { delay: number }>();

function getObserver(threshold: number): IntersectionObserver {
    let observer = observers.get(threshold);
    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const config = callbacks.get(entry.target);
                        const delay = config?.delay ?? 0;
                        if (delay > 0) {
                            setTimeout(() => {
                                entry.target.classList.add('reveal-visible');
                            }, delay);
                        } else {
                            entry.target.classList.add('reveal-visible');
                        }
                        observer!.unobserve(entry.target);
                        callbacks.delete(entry.target);
                    }
                });
            },
            { threshold }
        );
        observers.set(threshold, observer);
    }
    return observer;
}

interface ScrollRevealProps {
    children: ReactNode;
    className?: string;
    delay?: number;
    threshold?: number;
}

export default function ScrollReveal({
    children,
    className = '',
    delay = 0,
    threshold = 0.05
}: ScrollRevealProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = getObserver(threshold);
        callbacks.set(element, { delay });
        observer.observe(element);

        return () => {
            observer.unobserve(element);
            callbacks.delete(element);
        };
    }, [delay, threshold]);

    return (
        <div ref={ref} className={`reveal-hidden ${className}`}>
            {children}
        </div>
    );
}
